import { Router } from "express";
import type { Request, Response } from "express";
import { splayCache, setCachedExternal } from "./splay";

const router: Router = Router();

const BASE_URL = "https://streamhub.replit.app";
const SPLAY_KEY = process.env.SPLAY_API_KEY || "";
const SPLAY_API = "https://api.splay.id";

const SITEMAP_CACHE: Record<string, { xml: string; at: number }> = {};
const SITEMAP_TTL = 6 * 60 * 60 * 1000;
const SPLAY_CACHE_TTL = 30 * 60 * 1000;

function sleep(ms: number) {
  return new Promise((r) => setTimeout(r, ms));
}

async function splayFetchCached(path: string, retries = 3): Promise<any> {
  const url = `${SPLAY_API}${path}`;

  const cached = splayCache.get(url);
  if (cached && Date.now() - cached.ts < SPLAY_CACHE_TTL) {
    return cached.data;
  }

  for (let attempt = 0; attempt < retries; attempt++) {
    try {
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${SPLAY_KEY}` },
        signal: AbortSignal.timeout(30000),
      });
      if (res.status === 429) {
        const wait = (attempt + 1) * 5000;
        console.log(`[sitemap] rate limited on ${path}, retrying in ${wait}ms`);
        await sleep(wait);
        continue;
      }
      if (!res.ok) return null;
      const data = await res.json();
      setCachedExternal(url, data);
      return data;
    } catch {
      if (attempt < retries - 1) await sleep(2000);
    }
  }
  return null;
}

function getFromSplayCache(pathPattern: string): any[] {
  const items: any[] = [];
  const seen = new Set<string | number>();
  for (const [key, entry] of splayCache.entries()) {
    if (!key.includes(pathPattern)) continue;
    const d = entry.data as any;
    const rows = d?.data || (Array.isArray(d) ? d : null);
    if (!Array.isArray(rows)) continue;
    for (const item of rows) {
      const id = item?.id;
      if (id && !seen.has(id)) {
        seen.add(id);
        items.push(item);
      }
    }
  }
  return items;
}

function xmlEscape(s: string) {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function urlEntry(
  loc: string,
  opts: { lastmod?: string; changefreq?: string; priority?: string } = {},
) {
  const { changefreq = "weekly", priority = "0.6", lastmod } = opts;
  return [
    "  <url>",
    `    <loc>${xmlEscape(BASE_URL + loc)}</loc>`,
    lastmod ? `    <lastmod>${lastmod}</lastmod>` : "",
    `    <changefreq>${changefreq}</changefreq>`,
    `    <priority>${priority}</priority>`,
    "  </url>",
  ]
    .filter(Boolean)
    .join("\n");
}

function wrapUrlset(urls: string[]) {
  return [
    '<?xml version="1.0" encoding="UTF-8"?>',
    '<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">',
    ...urls,
    "</urlset>",
  ].join("\n");
}

const PAGE_DELAY = 2200;

async function fetchAllPages(
  apiBasePath: string,
  perPage: number,
  maxItems: number,
): Promise<any[]> {
  const items: any[] = [];
  const seen = new Set<string | number>();
  const maxPages = Math.ceil(maxItems / perPage);
  const sep = apiBasePath.includes("?") ? "&" : "?";

  for (let p = 1; p <= maxPages && items.length < maxItems; p++) {
    if (p > 1) await sleep(PAGE_DELAY);
    const data = await splayFetchCached(`${apiBasePath}${sep}per_page=${perPage}&page=${p}`);
    if (!data?.data?.length) break;
    for (const item of data.data) {
      if (!seen.has(item.id)) {
        seen.add(item.id);
        items.push(item);
      }
    }
    if (data.data.length < perPage) break;
  }
  return items.slice(0, maxItems);
}

async function generateDramaSitemap(): Promise<string> {
  const cached = getFromSplayCache("/api/dramas");
  const fetched = await fetchAllPages(
    "/api/dramas?sort_by=updated_at&sort_order=desc",
    200,
    2000,
  );
  const allItems = dedup([...cached, ...fetched], "id");
  console.log(`[sitemap] drama: ${cached.length} from cache + ${fetched.length} fetched = ${allItems.length} total`);
  return wrapUrlset(
    allItems.map((d) =>
      urlEntry(`/drama/${d.id}`, {
        lastmod: d.updated_at?.slice(0, 10) || d.created_at?.slice(0, 10),
        changefreq: "daily",
        priority: "0.7",
      }),
    ),
  );
}

async function generateAnimeSitemap(): Promise<string> {
  const cached = getFromSplayCache("/api/anime");
  const fetched = await fetchAllPages("/api/anime", 10, 300);
  const allItems = dedup([...cached, ...fetched], "id");
  console.log(`[sitemap] anime: ${cached.length} from cache + ${fetched.length} fetched = ${allItems.length} total`);
  return wrapUrlset(
    allItems.map((a) =>
      urlEntry(`/anime/${a.id}`, {
        lastmod: a.updated_at?.slice(0, 10),
        changefreq: "weekly",
        priority: "0.7",
      }),
    ),
  );
}

async function generateBacaSitemap(): Promise<string> {
  const cats = ["manhwa", "manga", "manhua"];
  const allUrls: string[] = [];
  for (const cat of cats) {
    const cached = getFromSplayCache(`/api/baca/${cat}`);
    const fetched = await fetchAllPages(`/api/baca/${cat}`, 200, 1500);
    const allItems = dedup([...cached, ...fetched], "id");
    console.log(`[sitemap] baca/${cat}: ${cached.length} cache + ${fetched.length} fetched = ${allItems.length}`);
    for (const t of allItems) {
      allUrls.push(
        urlEntry(`/baca/${cat}/${t.id}`, {
          lastmod: t.updated_at?.slice(0, 10),
          changefreq: "daily",
          priority: "0.7",
        }),
      );
    }
    await sleep(3000);
  }
  return wrapUrlset(allUrls);
}

async function generateIqiyiSitemap(): Promise<string> {
  const cached = getFromSplayCache("/api/iqiyi");
  const fetched = await fetchAllPages("/api/iqiyi", 200, 500);
  const allItems = dedup([...cached, ...fetched], "id");
  console.log(`[sitemap] iqiyi: ${cached.length} cache + ${fetched.length} fetched = ${allItems.length}`);
  return wrapUrlset(
    allItems.map((t) =>
      urlEntry(`/iqiyi/${t.id}`, {
        lastmod: t.updated_at?.slice(0, 10),
        changefreq: "weekly",
        priority: "0.7",
      }),
    ),
  );
}

async function generateMovieBoxSitemap(): Promise<string> {
  const cached = getFromSplayCache("/api/moviebox");
  const fetched = await fetchAllPages("/api/moviebox", 200, 2000);
  const allItems = dedup([...cached, ...fetched], "id");
  console.log(`[sitemap] moviebox: ${cached.length} cache + ${fetched.length} fetched = ${allItems.length}`);
  return wrapUrlset(
    allItems.map((t) =>
      urlEntry(`/moviebox/${t.id}`, {
        lastmod: t.updated_at?.slice(0, 10),
        changefreq: "weekly",
        priority: "0.7",
      }),
    ),
  );
}

async function generateDrakorSitemap(): Promise<string> {
  const cached = getFromSplayCache("/api/drakor");
  const fetched = await fetchAllPages("/api/drakor", 200, 1500);
  const allItems = dedup([...cached, ...fetched], "id");
  console.log(`[sitemap] drakor: ${cached.length} cache + ${fetched.length} fetched = ${allItems.length}`);
  return wrapUrlset(
    allItems.map((t) =>
      urlEntry(`/drakorindo/${t.id}`, {
        lastmod: t.updated_at?.slice(0, 10),
        changefreq: "daily",
        priority: "0.7",
      }),
    ),
  );
}

async function generateWeTVSitemap(): Promise<string> {
  const cached = getFromSplayCache("/api/wetv");
  const fetched = await fetchAllPages("/api/wetv", 200, 500);
  const allItems = dedup([...cached, ...fetched], "id");
  console.log(`[sitemap] wetv: ${cached.length} cache + ${fetched.length} fetched = ${allItems.length}`);
  return wrapUrlset(
    allItems.map((t) =>
      urlEntry(`/wetv/${t.id}`, {
        lastmod: t.updated_at?.slice(0, 10),
        changefreq: "weekly",
        priority: "0.7",
      }),
    ),
  );
}

function dedup<T>(items: T[], key: keyof T): T[] {
  const seen = new Set();
  return items.filter((item) => {
    const k = item[key];
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}

const GENERATORS: Record<string, () => Promise<string>> = {
  drama: generateDramaSitemap,
  anime: generateAnimeSitemap,
  baca: generateBacaSitemap,
  iqiyi: generateIqiyiSitemap,
  moviebox: generateMovieBoxSitemap,
  drakorindo: generateDrakorSitemap,
  wetv: generateWeTVSitemap,
};

async function backgroundRefreshAll() {
  for (const [key, gen] of Object.entries(GENERATORS)) {
    try {
      const xml = await gen();
      SITEMAP_CACHE[key] = { xml, at: Date.now() };
    } catch (e) {
      console.error(`[sitemap] error generating ${key}:`, e);
    }
    await sleep(3000);
  }
}

setTimeout(() => {
  backgroundRefreshAll().catch(() => {});
  setInterval(() => {
    backgroundRefreshAll().catch(() => {});
  }, SITEMAP_TTL);
}, 120000);

function serveXml(res: Response, xml: string) {
  res.setHeader("Content-Type", "application/xml; charset=utf-8");
  res.setHeader("Cache-Control", "public, max-age=21600");
  res.send(xml);
}

const GENERATING: Record<string, boolean> = {};

function makeSitemapHandler(key: string, gen: () => Promise<string>) {
  return (_req: Request, res: Response) => {
    const now = Date.now();
    const cached = SITEMAP_CACHE[key];

    if (cached && now - cached.at < SITEMAP_TTL) {
      return serveXml(res, cached.xml);
    }

    if (cached) {
      serveXml(res, cached.xml);
      if (!GENERATING[key]) {
        GENERATING[key] = true;
        gen()
          .then((xml) => { SITEMAP_CACHE[key] = { xml, at: Date.now() }; })
          .catch(() => {})
          .finally(() => { GENERATING[key] = false; });
      }
      return;
    }

    serveXml(res, wrapUrlset([]));

    if (!GENERATING[key]) {
      GENERATING[key] = true;
      gen()
        .then((xml) => { SITEMAP_CACHE[key] = { xml, at: Date.now() }; })
        .catch(() => {})
        .finally(() => { GENERATING[key] = false; });
    }
  };
}

router.get("/sitemap/drama.xml", makeSitemapHandler("drama", generateDramaSitemap));
router.get("/sitemap/anime.xml", makeSitemapHandler("anime", generateAnimeSitemap));
router.get("/sitemap/baca.xml", makeSitemapHandler("baca", generateBacaSitemap));
router.get("/sitemap/iqiyi.xml", makeSitemapHandler("iqiyi", generateIqiyiSitemap));
router.get("/sitemap/moviebox.xml", makeSitemapHandler("moviebox", generateMovieBoxSitemap));
router.get("/sitemap/drakorindo.xml", makeSitemapHandler("drakorindo", generateDrakorSitemap));
router.get("/sitemap/wetv.xml", makeSitemapHandler("wetv", generateWeTVSitemap));

export default router;
